import type { ZudokuPlugin } from "zudoku";

interface PageMeta {
  title: string;
  description: string;
  category?: {
    name: string;
    path: string;
  };
}

const SITE_NAME = "Gamopanda Documentation";
const SITE_URL = "https://gamopanda.com/docs";
const BASE_ORIGIN = "https://gamopanda.com";
const LOGO_URL = "https://static.gamopanda.com/commons/gamopanda-horizontal-light-logo.png";
const ORG_NAME = "Gamopanda";
const ORG_DESCRIPTION =
  "Omnichannel customer loyalty, retention, and gamification platform.";

const SAME_AS = [
  "https://www.linkedin.com/company/gamopanda",
  "https://www.youtube.com/channel/UChM7C6fuBmGhUuRqEBzGvwA",
];

const moduleCategory = { name: "Modules", path: "/modules-introduction" };
const apiCategory = { name: "API", path: "/api-introduction" };
const integrationsCategory = { name: "Integrations", path: "/integrations-introduction" };
const endUserDisplayCategory = { name: "End User Display", path: "/end-user-display-introduction" };

const routeMetadata: Record<string, PageMeta> = {
  "/": {
    title: "Gamopanda Documentation Introduction",
    description: "Get started with the Gamopanda modules, REST API, MCP Server, Integrations, Templates, Notifications and more."
  },
  "/introduction": {
    title: "Gamopanda Documentation Introduction",
    description: "Get started with the Gamopanda modules, REST API, MCP Server, Integrations, Templates, Notifications and more."
  },
  "/organization": {
    title: "Organization Module Documentation",
    description: "Configure high-level organization settings, including billing/trial status and organization name.",
    category: moduleCategory,
  },
  "/account": {
    title: "Account Module Documentation",
    description: "Configure core tenant settings for your loyalty program, including supported languages, timezone, and cart integrations.",
    category: moduleCategory,
  },
  "/member": {
    title: "Member Module Documentation",
    description: "Register and manage the end users who participate in your streaks, challenges, and reward programmes.",
    category: moduleCategory,
  },
  "/streak": {
    title: "Streak Module Documentation",
    description: "Boost customer engagement through daily, weekly, or monthly activity streaks with milestone rewards.",
    category: moduleCategory,
  },
  "/challenge": {
    title: "Challenge Module Documentation",
    description: "Drive customer engagement with time-bound challenges that reward users for hitting a cumulative activity target.",
    category: moduleCategory,
  },
  "/mission": {
    title: "Mission Module Documentation",
    description: "Turn repeat actions into exciting missions with achievable goals and rewarding milestones.",
    category: moduleCategory,
  },
  "/milestone": {
    title: "Milestone Module Documentation",
    description: "Define reward checkpoints within streaks and challenges that automatically issue rewards when users reach a target value.",
    category: moduleCategory,
  },
  "/reward": {
    title: "Reward Module Documentation",
    description: "Reward your customers for their loyalty with exclusive rewards and discounts.",
    category: moduleCategory,
  },
  "/coupon": {
    title: "Coupon Module Documentation",
    description: "Create and manage coupons to reward your customers for their loyalty and engagement.",
    category: moduleCategory,
  },
  "/reward-log": {
    title: "Reward Log Module Documentation",
    description: "System-generated records that track when a milestone reward was issued, and expired for each end user.",
    category: moduleCategory,
  },
  "/streak-log": {
    title: "Streak Log Module Documentation",
    description: "Record individual activity events that contribute to a user's progress toward completing a streak period.",
    category: moduleCategory,
  },
  "/streak-progress": {
    title: "Streak Progress Module Documentation",
    description: "Per-user progress records that track the overall state of each end user's journey through a streak — including current progress, status, and key timestamps.",
    category: moduleCategory,
  },
  "/challenge-log": {
    title: "Challenge Log Module Documentation",
    description: "Record individual activity events that contribute to a user's progress toward completing a challenge.",
    category: moduleCategory,
  },
  "/challenge-progress": {
    title: "Challenge Progress Module Documentation",
    description: "Per-user progress records that track the overall state of each end user's journey through a challenge — including cumulative progress, status, and key timestamps.",
    category: moduleCategory,
  },
  "/mission-log": {
    title: "Mission Log Module Documentation",
    description: "Record individual activity events that contribute to a user's progress toward completing a mission.",
    category: moduleCategory,
  },
  "/mission-progress": {
    title: "Mission Progress Module Documentation",
    description: "Per-user progress records that track the overall state of each end user's journey through a mission — including status, task progress, and key timestamps.",
    category: moduleCategory,
  },
  "/template": {
    title: "End User Template Module Documentation",
    description: "Customise all end-user-facing copy and branding shown in the Gamopanda widget — titles, button labels, empty-state messages, reward icons, and more.",
    category: moduleCategory,
  },
  "/theme": {
    title: "End User Display Theme Module Documentation",
    description: "Control the visual appearance of the Gamopanda end-user widget — colours, typography, button styles, and card styling — all in one place.",
    category: moduleCategory,
  },
  "/floating-widget": {
    title: "Floating Widget Module Documentation",
    description: "Configure the embeddable loyalty widget — its position on screen, icon, title, colours, and typography — for both authenticated users and guests.",
    category: moduleCategory,
  },
  "/email-header": {
    title: "Email Header Module Documentation",
    description: "Customise the HTML headers for transactional emails sent to your members to match your brand's style and logo.",
    category: moduleCategory,
  },
  "/email-footer": {
    title: "Email Footer Module Documentation",
    description: "Customise the HTML footers for transactional emails sent to your members to include unsubscribe links, terms, and copyright info.",
    category: moduleCategory,
  },
  "/email-template": {
    title: "Email Template Module Documentation",
    description: "Customise transactional email body templates, subjects, and layouts with dynamic placeholders.",
    category: moduleCategory,
  },
  "/email-theme": {
    title: "Email Theme Module Documentation",
    description: "Define reusable CSS stylesheets and visual styling rules applied across transactional email templates.",
    category: moduleCategory,
  },
  "/push-notification-template": {
    title: "Push Notification Template Module Documentation",
    description: "Customise push notification titles, body copy, and deep links with dynamic placeholders for automated mobile and web push notifications.",
    category: moduleCategory,
  },
  "/sms-template": {
    title: "SMS Template Module Documentation",
    description: "Customise transactional SMS message body content with dynamic placeholders for automated outgoing text communications.",
    category: moduleCategory,
  },
  "/whatsapp-template": {
    title: "WhatsApp Template Module Documentation",
    description: "Configure WhatsApp message templates linked to provider integrations for automated outgoing WhatsApp communications.",
    category: moduleCategory,
  },
  "/aws-whatsapp": {
    title: "AWS WhatsApp Integration Documentation",
    description: "Manage AWS WhatsApp credentials and account settings for sending transactional WhatsApp messages to members.",
    category: moduleCategory,
  },
  "/aws-sms": {
    title: "AWS SMS Integration Documentation",
    description: "Manage AWS SMS credentials and sender configuration for sending transactional SMS notifications to members.",
    category: moduleCategory,
  },
  "/firebase-cloud-messaging": {
    title: "Firebase Cloud Messaging Integration Documentation",
    description: "Configure Firebase Cloud Messaging (FCM) credentials to dispatch push notifications to mobile and web clients.",
    category: moduleCategory,
  },
  "/gamopanda-email": {
    title: "Gamopanda Email Integration Documentation",
    description: "Manage Gamopanda Email sender domains, DKIM, SPF, and custom MAIL FROM settings for transactional email dispatch.",
    category: moduleCategory,
  },
  "/notification": {
    title: "Notification Module Documentation",
    description: "Configure active notification rules mapping event triggers to multi-channel templates (Email, Push, SMS, WhatsApp) and provider integrations for automated outgoing communications.",
    category: moduleCategory,
  },
  "/notification-log": {
    title: "Notification Log Module Documentation",
    description: "Track and audit the delivery status, trigger source, and history of transactional notifications sent to your members.",
    category: moduleCategory,
  },
  "/webhook": {
    title: "Webhook Module Documentation",
    description: "Subscribe to platform loyalty events and receive HTTP POST callbacks at your endpoint in real-time.",
    category: moduleCategory,
  },
  "/webhook-log": {
    title: "Webhook Log Module Documentation",
    description: "Audit and debug the delivery status, response codes, and retry history of webhook callbacks sent to your application.",
    category: moduleCategory,
  },
  "/event-log": {
    title: "Event Log Module Documentation",
    description: "Audit the primary ledger of system-level loyalty events triggered by member progress and activity.",
    category: moduleCategory,
  },
  "/api-key": {
    title: "API Key Module Documentation",
    description: "Create and manage the API key and secret pairs used to authenticate server-to-server requests to the Gamopanda API.",
    category: moduleCategory,
  },
  "/api-introduction": {
    title: "Gamopanda REST API Overview",
    description: "Get started with the Gamopanda REST API documentation",
    category: apiCategory,
  },
  "/api": {
    title: "API Reference",
    description: "Interactive OpenAPI documentation and endpoint reference for Gamopanda REST API.",
    category: apiCategory,
  },
  "/mcp-server": {
    title: "MCP Server Integration Documentation",
    description: "Connect Gamopanda to AI coding assistants (Antigravity, Cursor, Claude Desktop, VS Code, Codex) using the Model Context Protocol (MCP) server endpoint.",
    category: integrationsCategory,
  },
  "/shopify": {
    title: "Shopify Integration Documentation",
    description: "Connect Gamopanda streaks, challenges, and rewards directly to your Shopify store with native Flow actions and an embeddable loyalty widget.",
    category: integrationsCategory,
  },
  "/wix": {
    title: "Wix Integration Documentation",
    description: "Connect Gamopanda streaks, challenges, and rewards directly to your Wix store with native Automations actions and an embeddable loyalty widget.",
    category: integrationsCategory,
  },
  "/javascript-integration": {
    title: "End User Display Integration Documentation",
    description: "Embed the Gamopanda End User Display on any website using the widget.js snippet.",
    category: endUserDisplayCategory,
  },
  "/access-token": {
    title: "End User Access Token Generation Documentation",
    description: "How to generate a signed JWT access token for an end user so the Gamopanda End User Display can display their personalised progress.",
    category: endUserDisplayCategory,
  },
  "/demo": {
    title: "Try the Gamopanda Demo",
    description: "See Gamopanda in action on a live Shopify store",
  },
};

export function buildJsonLd(pathname: string) {
  const cleanPath = pathname.replace(/^\/docs/, "") || "/";
  const normalizedPath = cleanPath === "" ? "/" : cleanPath;
  const canonicalUrl = `${BASE_ORIGIN}/docs${normalizedPath === "/" ? "" : normalizedPath}`;

  const meta = routeMetadata[normalizedPath] || {
    title: "Gamopanda Documentation",
    description: "Gamopanda documentation, REST API, MCP Server and Integration Guides.",
  };

  const orgSchema = {
    "@type": "Organization",
    "@id": `${BASE_ORIGIN}#organization`,
    name: ORG_NAME,
    url: BASE_ORIGIN,
    logo: LOGO_URL,
    sameAs: SAME_AS,
    description: ORG_DESCRIPTION,
  };

  const websiteSchema = {
    "@type": "WebSite",
    "@id": `${SITE_URL}#website`,
    url: SITE_URL,
    name: SITE_NAME,
    description: "Comprehensive documentation, API references, guides, and integration tutorials for Gamopanda.",
    publisher: {
      "@id": `${BASE_ORIGIN}#organization`,
    },
    isPartOf: {
      "@id": `${BASE_ORIGIN}#website`,
    },
    inLanguage: "en-US",
  };

  const breadcrumbItems: any[] = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: BASE_ORIGIN
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Documentation",
      item: SITE_URL,
    },
  ];

  const categoryUrl = meta.category ? `${SITE_URL}${meta.category.path}` : null;
  if (meta.category && categoryUrl !== canonicalUrl && categoryUrl !== SITE_URL) {
    breadcrumbItems.push({
      "@type": "ListItem",
      position: breadcrumbItems.length + 1,
      name: meta.category.name,
      item: categoryUrl,
    });
  }

  if (canonicalUrl !== SITE_URL) {
    breadcrumbItems.push({
      "@type": "ListItem",
      position: breadcrumbItems.length + 1,
      name: meta.title,
      item: canonicalUrl,
    });
  }

  const breadcrumbSchema = {
    "@type": "BreadcrumbList",
    "@id": `${canonicalUrl}#breadcrumb`,
    itemListElement: breadcrumbItems,
  };

  const articleSchema = {
    "@type": "TechArticle",
    "@id": `${canonicalUrl}#article`,
    url: canonicalUrl,
    name: meta.title,
    headline: meta.title,
    description: meta.description,
    isPartOf: {
      "@id": `${SITE_URL}#website`,
    },
    author: {
      "@id": `${BASE_ORIGIN}#organization`,
    },
    publisher: {
      "@id": `${BASE_ORIGIN}#organization`,
    },
    inLanguage: "en-US",
    mainEntityOfPage: canonicalUrl,
    breadcrumb: {
      "@id": `${canonicalUrl}#breadcrumb`,
    },
  };

  return {
    "@context": "https://schema.org",
    "@graph": [orgSchema, websiteSchema, breadcrumbSchema, articleSchema],
  };
}

export const jsonLdPlugin: ZudokuPlugin = {
  getHead: ({ location }) => {
    const jsonLdData = buildJsonLd(location.pathname);
    return (
      <script type="application/ld+json">{JSON.stringify(jsonLdData)}</script>
    );
  },
};
